import SparkMD5 from "spark-md5";
import { allowedFileTypesToUpload } from "../constants/upload";
import { UPLOAD_OPTIONS } from "../constants/common";
import { generateUUID } from "./uuid";

export const MAX_FILE_SIZE = 4000 * 1024 * 1024; // 4000MB in bytes
export const MIN_FILE_SIZE = 1024; // 1 KB
const MD5_SIZE_LIMIT = UPLOAD_OPTIONS?.MD5_SIZE_LIMIT || 50 * 1024 * 1024; // 50MB

export const isFileSizeValid = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

export const isFileSizeTooSmall = (file: File): boolean => {
  return file.size <= MIN_FILE_SIZE;
};

export const formatFileSize = (bytes: number): string => {
  const sizes = ["Bytes", "KB", "MB", "GB"];
  if (bytes === 0) return "0";
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
  return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i];
};

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  // Legacy binary .doc (application/msword) is intentionally NOT accepted for upload — it
  // cannot be previewed in the browser. Only modern .docx (OOXML) is supported.
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  // "application/vnd.ms-excel",
  // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

// Image extensions that are intentionally NOT accepted for upload even though
// they match the "image/*" wildcard — browsers cannot render/preview them.
const DISALLOWED_EXTENSIONS = ["tif", "tiff"];

/**
 * validate uploading file type with special mime types
 * @param file File object
 * @returns boolean
 **/
export function isValidFileTypeToUpload(file: File) {
  const fileType = file?.type;
  const fileExtension = file.name?.split(".").pop()?.toLowerCase() ?? "";

  // Explicitly reject disallowed extensions (e.g. .tif/.tiff) that would
  // otherwise slip through via the "image/*" wildcard match below.
  if (DISALLOWED_EXTENSIONS.includes(fileExtension)) return false;

  return allowedFileTypesToUpload?.some((allowedType) => {
    // Handle wildcard types (e.g., "image/*", "video/*")
    if (allowedType.endsWith("/*") && !allowedType.startsWith("application/")) {
      const category = allowedType.split("/")[0];
      return fileType?.startsWith(`${category}/`);
    }

    // Handle extensions (e.g., ".doc")
    if (allowedType.startsWith(".")) {
      const allowedExt = allowedType.substring(1).toLowerCase();
      return fileExtension === allowedExt;
    }

    // Exact MIME type match (e.g., "application/pdf")
    if (fileType === allowedType) return true;

    // Fallback for application types if application/* is explicitly allowed
    if ((allowedType as string) === "application/*") {
      return ALLOWED_MIME_TYPES.some((spType) => fileType === spType);
    }

    return false;
  });
}

function formatExtension(entry: string): string {
  const trimmed = entry.trim();
  if (trimmed.includes("/*")) return trimmed.split("/")[0]; // "image/*" --> "image"
  if (trimmed.includes("/")) return trimmed.split("/")[1]; // "application/pdf" --> "pdf"
  return trimmed.replace(".", ""); // ".doc" --> "doc"
}

function toTextTransform(str: string) {
  // if (str.length <= 3) return str.toUpperCase();
  return `${str[0]?.toUpperCase()}${str.slice(1, str.length).toLowerCase()}`;
}

/**
 *  Format file extensions and make final string
 *  @param extentionStr string
 *  @return string
 **/
export function formatAllowedFileTypes(extentionStr: string): string {
  const labels = [...new Set(extentionStr?.split(",").map((entry) => toTextTransform(formatExtension(entry))))];

  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];

  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

type CalculateMD5Options = { abort?: AbortSignal; size?: number };
/**
 * Calculate MD5 hash for given file
 * @param {File} file
 * @param {Object|AbortSignal} options
 *      size: size to calculate hash
 *      abort: cancellation signal
 *  @returns {Unknown}  Promise<unknown>
 * */
export async function calculateMD5(file: File, option?: AbortSignal): Promise<unknown>;
export async function calculateMD5(file: File, option?: CalculateMD5Options): Promise<unknown>;
export async function calculateMD5(file: File, option?: AbortSignal | CalculateMD5Options) {
  const normalizedOption = option instanceof AbortSignal ? { abort: option } : option || {};
  const { abort, size = MD5_SIZE_LIMIT } = normalizedOption;
  if (file.size > size) {
    console.warn(`[MD5] Skipping hash calculation for ${file.name} (${formatFileSize(file.size)}) - file too large`);
    return "skipped-large-file";
  }

  const startTime = performance.now();

  return new Promise((resolve, reject) => {
    abort?.throwIfAborted();
    const chunkSize = 2097152; // Read in chunks of 2MB
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      spark.append(e.target!.result as ArrayBuffer);
      currentChunk++;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        const hash = spark.end();
        const endTime = performance.now();
        console.log(`[MD5] Hash calculation for ${file.name} (${formatFileSize(file.size)}) took ${(endTime - startTime).toFixed(2)}ms`);
        resolve(hash);
      }
    };

    fileReader.onerror = (e) => {
      reject(e);
    };

    function loadNext() {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize;
      fileReader.readAsArrayBuffer(file.slice(start, end));
    }

    loadNext();
  });
}

export function prepareUploadFileDetail(file: File) {
  const fileType = file.type;
  const fileTempId = generateUUID();

  return {
    id: fileTempId,
    type: fileType,
    name: file.name,
    description: "",
    file: file,
    original_file: {
      filename: file.name,
      type: file.type,
      size: file.size?.toString()
    },
    relationships: {
      byTableSlug: {},
      media: []
    }
  };
}
